import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { fmt } from '@/features/notas/calculo';
import type { DisciplinaDesempenho } from '@/features/notas/types';

/**
 * PDM · RolêRadar em React: a nota vira o "MeuPonto" e cada item (atividades da AWS e itens da prova) vira um lugar.
 * A "distância" de um lugar é o quanto faltou para a nota cheia naquele item, formatada como na Fase 9.
 */

type Categoria = 'aws' | 'prova' | 'todas';
const CATEGORIAS: { chave: Categoria; rotulo: string }[] = [
  { chave: 'todas', rotulo: 'Tudo' },
  { chave: 'aws', rotulo: 'Atividades da AWS' },
  { chave: 'prova', rotulo: 'Itens da prova' },
];

interface Lugar {
  id: string;
  nome: string;
  categoria: Exclude<Categoria, 'todas'>;
  distancia: number; // pontos que faltaram para a nota cheia do item
  detalhe: string;
}

/** Como o formatarDistancia da F9: "a 0,25 ponto" / "a 1,5 pontos" / "aqui mesmo". */
function formatarDistancia(pontos: number) {
  if (pontos <= 0.0049) return 'aqui mesmo (nota cheia)';
  return `a ${pontos.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${pontos <= 1 ? 'ponto' : 'pontos'}`;
}

export function RolesPerto({ d }: { d: DisciplinaDesempenho }) {
  const [agora, setAgora] = useState(() => Date.now());
  const [desde, setDesde] = useState(() => Date.now());
  const [raio, setRaio] = useState('1');
  const [categoria, setCategoria] = useState<Categoria>('todas');
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState<{ raio: number; categoria: Categoria } | null>(null);

  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(t); // como no componentWillUnmount do MeuPonto
  }, []);

  const lugares: Lugar[] = useMemo(() => {
    const corte = d.corteAws ?? 0.7;
    const aws: Lugar[] = d.itensT1.map((i) => {
      const vale = (i.aluno ?? 0) >= corte - 1e-9 ? 10 : 0;
      return {
        id: i.id,
        nome: i.nome,
        categoria: 'aws',
        distancia: ((10 - vale) / Math.max(d.itensT1.length, 1)) * 0.3,
        detalhe: `${Math.round((i.aluno ?? 0) * 100)}% no AWS Academy · ${vale ? 'aprovado' : `abaixo de ${Math.round(corte * 100)}%`}`,
      };
    });
    const prova: Lugar[] = d.requisitos
      .filter((r) => r.peso && r.grupo)
      .map((r) => ({
        id: r.id,
        nome: r.nome,
        categoria: 'prova',
        distancia: ((r.peso ?? 0) - (r.grupo?.nota ?? 0)) * 0.2,
        detalhe: `grupo: ${fmt(r.grupo?.nota)} de ${fmt(r.peso)} no item`,
      }));
    return [...aws, ...prova];
  }, [d]);

  const buscar = (e: FormEvent) => {
    e.preventDefault();
    const n = Number(raio.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0 || n > 10) {
      setErro('O raio deve ser um número entre 0 e 10 pontos.');
      return;
    }
    setErro(null);
    setBusca({ raio: n, categoria });
  };

  const resultado = busca
    ? lugares
        .filter(
          (l) =>
            (busca.categoria === 'todas' || l.categoria === busca.categoria) &&
            l.distancia <= busca.raio + 1e-9,
        )
        .sort((a, b) => b.distancia - a.distancia)
    : [];
  const perdidos = resultado.filter((l) => l.distancia > 0.0049);
  const segundos = Math.max(0, Math.floor((agora - desde) / 1000));
  const est = d.turmaInfo.componentes.P1;

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <MyLocationRounded color="primary" />
            <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
              MeuPonto
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshRounded />}
              onClick={() => setDesde(Date.now())}
            >
              Atualizar
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            Latitude (P1): {(d.notas.P1 ?? 0).toFixed(4)} · Longitude (posição na turma):{' '}
            {(est?.percentil ?? 0).toFixed(4)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nota obtida há {segundos} {segundos === 1 ? 'segundo' : 'segundos'}. Hemisfério{' '}
            {(d.notas.P1 ?? 0) >= 6 ? 'Norte (acima de 6)' : 'Sul (abaixo de 6)'}.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            Onde ficaram os seus pontos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cada atividade da AWS e cada item da prova é um "lugar". A distância é quanto da P1
            ficou de fora naquele lugar (já com o peso da parte). Escolha a categoria e o raio e
            busque, como no formulário da Fase 8.
          </Typography>
          <Box component="form" noValidate onSubmit={buscar}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
              {CATEGORIAS.map((c) => (
                <Chip
                  key={c.chave}
                  label={c.rotulo}
                  color={categoria === c.chave ? 'primary' : 'default'}
                  variant={categoria === c.chave ? 'filled' : 'outlined'}
                  onClick={() => setCategoria(c.chave)}
                />
              ))}
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'flex-start' } }}
            >
              <TextField
                size="small"
                label="Raio"
                value={raio}
                onChange={(e) => setRaio(e.target.value)}
                error={Boolean(erro)}
                helperText={erro ?? 'Em pontos da P1 (0 a 10).'}
                slotProps={{
                  input: { endAdornment: <InputAdornment position="end">pontos</InputAdornment> },
                }}
                sx={{ width: 200 }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchRounded />}
                sx={{ mt: { sm: 0.25 } }}
              >
                Buscar
              </Button>
            </Stack>
          </Box>
          {busca && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {resultado.length}{' '}
                {resultado.length === 1 ? 'lugar encontrado' : 'lugares encontrados'} a até{' '}
                {busca.raio.toLocaleString('pt-BR')} {busca.raio <= 1 ? 'ponto' : 'pontos'}
                {perdidos.length ? ` · ${perdidos.length} com pontos de fora` : ''}
              </Typography>
              {resultado.length === 0 && (
                <Alert severity="info">Nenhum lugar neste raio. Tente aumentar.</Alert>
              )}
              <Grid container spacing={1.5}>
                {resultado.map((l) => (
                  <Grid key={l.id} size={{ xs: 12, sm: 6 }}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0,
                              bgcolor: l.distancia > 0.0049 ? 'warning.light' : 'primary.container',
                            }}
                          >
                            <PlaceRounded fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {l.nome}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                              {l.categoria === 'aws' ? 'AWS' : 'Prova'} · {l.detalhe}
                            </Typography>
                            <Typography
                              variant="body2"
                              color={l.distancia > 0.0049 ? 'warning.main' : 'success.main'}
                            >
                              {formatarDistancia(l.distancia)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
